import { useLocalSearchParams, useRouter } from 'expo-router';
import type { TFunction } from 'i18next';
import React, { useState } from 'react';
import { Modal, Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { BudgetMeter } from '@/components/fantasy/BudgetMeter';
import { PitchView } from '@/components/fantasy/PitchView';
import { PlayerPickerModal } from '@/components/fantasy/PlayerPickerModal';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { TextField } from '@/components/ui/TextField';
import {
  useFantasyPlayerPool,
  useFantasySeasons,
  useMyFantasyTeam,
  useSaveSquad,
  useSquadPicks,
} from '@/features/fantasy/hooks';
import { parseFormationRules } from '@/features/fantasy/rules';
import {
  addPlayer,
  buildRandomDraft,
  draftCost,
  draftFromPicks,
  emptyDraft,
  removePlayer,
  setCaptain,
  setVice,
  swapPlayers,
  toRpcPicks,
  validateDraft,
  type DraftIssue,
  type SquadDraft,
} from '@/features/fantasy/squadModel';
import { SLOT_LABELS_442 } from '@/features/fantasy/worldCup';
import { useAuth } from '@/lib/auth/AuthProvider';
import type { FantasySeasonWithCompetition, PlayerWithTeam } from '@/lib/data/provider';
import { errorKey } from '@/lib/utils/errors';
import type { FormationRules, Position } from '@scoring/rules.ts';

function issueText(issue: DraftIssue, t: TFunction): string {
  switch (issue.type) {
    case 'needs':
      return t(`fantasy.builder.needs.${issue.position}`, { count: issue.count });
    case 'overBudget':
      return t('fantasy.builder.overBudget', { amount: issue.amount });
    case 'needCaptain':
      return t('fantasy.builder.needCaptain');
    case 'needVice':
      return t('fantasy.builder.needVice');
    case 'invalidFormation':
      return t('fantasy.builder.invalidFormation');
  }
}

export default function SquadBuilderScreen() {
  const params = useLocalSearchParams<{ seasonId: string; teamName?: string }>();
  const seasonId = params.seasonId;
  const router = useRouter();
  const { t } = useTranslation();
  const { userId } = useAuth();

  const seasonsQuery = useFantasySeasons();
  const season = (seasonsQuery.data ?? []).find((entry) => entry.id === seasonId);
  const poolQuery = useFantasyPlayerPool(season?.competition_id);
  const myTeamQuery = useMyFantasyTeam(seasonId);
  const picksQuery = useSquadPicks(myTeamQuery.data?.id);

  if (!userId) {
    return (
      <Screen>
        <BackHeader title={t('fantasy.builder.title')} />
        <EmptyState
          icon="lock-closed-outline"
          title={t('common.signInRequiredTitle')}
          body={t('common.signInRequiredBody')}
          actionLabel={t('auth.signIn')}
          onAction={() => router.push('/auth/sign-in')}
        />
      </Screen>
    );
  }

  const waitingForPicks = myTeamQuery.data !== null && myTeamQuery.data !== undefined
    ? picksQuery.isPending
    : myTeamQuery.isPending;

  if (seasonsQuery.isPending || poolQuery.isPending || waitingForPicks) {
    return (
      <Screen>
        <BackHeader title={t('fantasy.builder.title')} />
        <View className="gap-4 px-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-80 w-full" />
          <Skeleton className="h-24 w-full" />
        </View>
      </Screen>
    );
  }

  if (seasonsQuery.isError || !season) {
    return (
      <Screen>
        <BackHeader title={t('fantasy.builder.title')} />
        <ErrorState error={seasonsQuery.error ?? new Error('SEASON_NOT_FOUND')} />
      </Screen>
    );
  }

  if (poolQuery.isError) {
    return (
      <Screen>
        <BackHeader title={t('fantasy.builder.title')} />
        <ErrorState error={poolQuery.error} onRetry={() => poolQuery.refetch()} />
      </Screen>
    );
  }

  const initialDraft =
    picksQuery.data && picksQuery.data.length > 0
      ? draftFromPicks(picksQuery.data)
      : emptyDraft();
  const initialTeamName = params.teamName ?? myTeamQuery.data?.name ?? '';

  return (
    <SquadBuilderForm
      season={season}
      pool={poolQuery.data ?? []}
      initialDraft={initialDraft}
      initialTeamName={initialTeamName}
    />
  );
}

/** Mounted only once data is ready, so draft state initializes from props. */
function SquadBuilderForm({
  season,
  pool,
  initialDraft,
  initialTeamName,
}: {
  season: FantasySeasonWithCompetition;
  pool: PlayerWithTeam[];
  initialDraft: SquadDraft;
  initialTeamName: string;
}) {
  const router = useRouter();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const saveMutation = useSaveSquad();
  const rules: FormationRules = parseFormationRules(season.formation_rules);
  const isWorldCup = (season.competition?.name ?? '').toLowerCase().includes('world cup');

  const [draft, setDraft] = useState<SquadDraft>(initialDraft);
  const [teamName, setTeamName] = useState(initialTeamName);
  const [pickerPosition, setPickerPosition] = useState<Position | null>(null);
  const [actionPlayerId, setActionPlayerId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const pickedIds = new Set(draft.picks.map((pick) => pick.player.id));
  const issues = validateDraft(draft, rules, season.budget);
  const actionPick = actionPlayerId
    ? draft.picks.find((pick) => pick.player.id === actionPlayerId)
    : undefined;
  const swapCandidates = actionPick
    ? draft.picks.filter(
        (pick) =>
          pick.player.position === actionPick.player.position &&
          pick.isStarting !== actionPick.isStarting,
      )
    : [];

  const showFeedback = (message: string) => {
    setFeedback(message);
    setTimeout(() => setFeedback(null), 2500);
  };

  const handlePick = (player: PlayerWithTeam) => {
    const result = addPlayer(draft, player, rules);
    if (!result.ok) {
      showFeedback(
        result.reason === 'alreadyPicked'
          ? t('fantasy.builder.alreadyPicked')
          : t('fantasy.builder.positionFull', {
              position: t(`player.positions.${player.position}`),
            }),
      );
      return;
    }
    setDraft(result.draft);
    setPickerPosition(null);
  };

  const handleRandom = () => {
    const random = buildRandomDraft(pool, rules, season.budget);
    if (random) {
      setDraft(random);
      showFeedback(t('fantasy.builder.randomDone'));
    } else {
      showFeedback(t('fantasy.builder.randomFailed'));
    }
  };

  const handleSave = () => {
    setSaveError(null);
    saveMutation.mutate(
      { fantasySeasonId: season.id, teamName: teamName.trim(), picks: toRpcPicks(draft) },
      {
        onSuccess: () => {
          router.replace('/(tabs)/fantasy');
        },
        onError: (error) => {
          setSaveError(t(errorKey(error)));
        },
      },
    );
  };

  const canSave =
    issues.length === 0 &&
    teamName.trim().length >= 3 &&
    teamName.trim().length <= 30 &&
    !saveMutation.isPending;

  return (
    <Screen>
      <BackHeader title={t('fantasy.builder.title')} />
      <ScrollView contentContainerStyle={{ paddingBottom: insets.bottom + 120 }}>
        <View className="gap-4 px-4 pb-4">
          <TextField
            label={t('fantasy.onboarding.teamNameTitle')}
            placeholder={t('fantasy.onboarding.teamNamePlaceholder')}
            value={teamName}
            onChangeText={setTeamName}
            maxLength={30}
          />
          <Card className="px-4 py-3">
            <BudgetMeter
              spent={draftCost(draft)}
              budget={season.budget}
              pickedCount={draft.picks.length}
              squadSize={season.squad_size}
            />
          </Card>
          <Button
            label={t('fantasy.builder.random')}
            variant="secondary"
            onPress={handleRandom}
          />
        </View>

        <PitchView
          draft={draft}
          rules={rules}
          mode="edit"
          slotLabels={isWorldCup ? SLOT_LABELS_442 : undefined}
          onSwap={(a, b) => setDraft((current) => swapPlayers(current, a, b))}
          onChipPress={setActionPlayerId}
          onEmptySlotPress={(position) => setPickerPosition(position)}
        />

        {issues.length > 0 ? (
          <Card className="mx-4 mt-4 gap-1.5 px-4 py-3">
            {issues.map((issue, index) => (
              <AppText key={index} variant="micro" className="text-volt">
                · {issueText(issue, t)}
              </AppText>
            ))}
          </Card>
        ) : null}
        {feedback ? (
          <Card className="mx-4 mt-3 bg-surface-2 px-4 py-3">
            <AppText variant="micro" className="text-live">
              {feedback}
            </AppText>
          </Card>
        ) : null}
        {saveError ? (
          <Card className="mx-4 mt-3 border-loss/40 px-4 py-3">
            <AppText variant="micro" className="text-loss">
              {saveError}
            </AppText>
          </Card>
        ) : null}
      </ScrollView>

      {/* Sticky save bar */}
      <View
        className="absolute inset-x-0 bottom-0 border-t border-line bg-surface px-4 pt-3"
        style={{ paddingBottom: insets.bottom + 12 }}
      >
        <Button
          label={
            saveMutation.isPending ? t('fantasy.builder.saving') : t('fantasy.builder.saveSquad')
          }
          loading={saveMutation.isPending}
          disabled={!canSave}
          onPress={handleSave}
        />
      </View>

      <PlayerPickerModal
        visible={pickerPosition !== null}
        position={pickerPosition}
        pool={pool}
        pickedIds={pickedIds}
        onPick={handlePick}
        onClose={() => setPickerPosition(null)}
      />

      {/* Player action sheet */}
      <Modal
        visible={actionPick !== undefined}
        animationType="fade"
        transparent
        onRequestClose={() => setActionPlayerId(null)}
      >
        <Pressable
          className="flex-1 justify-end bg-black/60"
          onPress={() => setActionPlayerId(null)}
        >
          <Pressable
            onPress={(event) => event.stopPropagation()}
            className="rounded-t-3xl border border-line bg-surface-2 px-4 pt-4"
            style={{ paddingBottom: insets.bottom + 16 }}
          >
            {actionPick ? (
              <View className="gap-2.5">
                <AppText variant="heading" className="mb-1">
                  {actionPick.player.known_as ?? actionPick.player.name}
                </AppText>
                {actionPick.isStarting ? (
                  <>
                    <Button
                      label={t('fantasy.builder.makeCaptain')}
                      variant="secondary"
                      compact
                      onPress={() => {
                        setDraft((current) => setCaptain(current, actionPick.player.id));
                        setActionPlayerId(null);
                      }}
                    />
                    <Button
                      label={t('fantasy.builder.makeVice')}
                      variant="secondary"
                      compact
                      onPress={() => {
                        setDraft((current) => setVice(current, actionPick.player.id));
                        setActionPlayerId(null);
                      }}
                    />
                  </>
                ) : null}
                {swapCandidates.map((candidate) => (
                  <Button
                    key={candidate.player.id}
                    label={`${
                      actionPick.isStarting
                        ? t('fantasy.builder.moveToBench')
                        : t('fantasy.builder.moveToStarting')
                    } ⇄ ${candidate.player.known_as ?? candidate.player.name}`}
                    variant="secondary"
                    compact
                    onPress={() => {
                      setDraft((current) =>
                        swapPlayers(current, actionPick.player.id, candidate.player.id),
                      );
                      setActionPlayerId(null);
                    }}
                  />
                ))}
                <Button
                  label={t('fantasy.builder.remove')}
                  variant="danger"
                  compact
                  onPress={() => {
                    setDraft((current) => removePlayer(current, actionPick.player.id));
                    setActionPlayerId(null);
                  }}
                />
                <Button
                  label={t('common.cancel')}
                  variant="ghost"
                  compact
                  onPress={() => setActionPlayerId(null)}
                />
              </View>
            ) : null}
          </Pressable>
        </Pressable>
      </Modal>
    </Screen>
  );
}
